/**
 * The last roster on this device (slice 11): a convenience only. Saved builds go to the account in
 * slice 12, and iOS evicts site storage after 7 idle days, so nothing depends on it. It goes
 * through `readPref` / `writePref`, which survive a throwing localStorage; what it reads back is
 * checked against the loaded pack, and anything that does not fit is dropped (an unknown species
 * or move, a gen not offered); if anything was dropped the charts are proposed again. Bad or
 * missing data gives the empty screen.
 *
 * Slice 11h: the charts remap through the old → new pick index map, so a dropped pick moves the
 * others' entries, never their Pokémon; the holds (`held`) are kept where they still name a known
 * slot with a pick in it, a save without them loads with none, and a chart that does not fit
 * clears them before the proposal. The fallback says nothing on screen.
 */
import { DEFENSE_SLOTS, OFFENSE_SLOTS, type Difficulty, type RosterSlot } from '@tdmons/sim';
import { readPref, writePref } from '../ui/sheet';
import {
  BAG_SIZE,
  MATCH_LEVELS,
  ROSTER_SIZE,
  bagItemIds,
  emptyState,
  recommend,
  type PackIndex,
  type RosterPick,
  type RosterState,
} from './state';

export const ROSTER_KEY = 'tdmons.quickplay';
const VERSION = 1;

export function saveRoster(state: RosterState): void {
  writePref(ROSTER_KEY, JSON.stringify({ v: VERSION, ...state }));
}

/** The gens a saved roster asked for, if any are offered; else `fallback`. */
export function savedGens(offered: readonly number[], fallback: readonly number[]): number[] {
  const raw = parse();
  const gens = Array.isArray(raw?.gens)
    ? (raw.gens as unknown[]).filter((g): g is number => offered.includes(g as number))
    : [];
  return gens.length > 0 ? gens : [...fallback];
}

function parse(): Record<string, unknown> | null {
  const text = readPref(ROSTER_KEY);
  if (!text) return null;
  try {
    const raw: unknown = JSON.parse(text);
    if (!raw || typeof raw !== 'object' || (raw as { v?: unknown }).v !== VERSION) return null;
    return raw as Record<string, unknown>;
  } catch {
    return null;
  }
}

const isChart = (x: unknown): x is Record<string, number> =>
  !!x &&
  typeof x === 'object' &&
  Object.values(x as Record<string, unknown>).every((v) => Number.isInteger(v));

/** The saved roster as it fits `pack`, or the empty screen for its gens. */
export function loadRoster(pack: PackIndex): RosterState {
  const empty = emptyState(pack.gens);
  const raw = parse();
  if (!raw) return empty;
  const picks: RosterPick[] = [];
  /** Saved pick index → index in `picks` (slice 11h: both charts remap through it). */
  const kept = new Map<number, number>();
  let dropped = false;
  const saved = Array.isArray(raw.picks) ? (raw.picks as unknown[]) : [];
  for (const [at, p] of saved.entries()) {
    const pick = p as Partial<RosterPick> | null;
    const creature = typeof pick?.id === 'string' ? pack.byId.get(pick.id) : undefined;
    if (!creature || picks.length >= ROSTER_SIZE || picks.some((x) => x.id === creature.id)) {
      dropped = true;
      continue;
    }
    kept.set(at, picks.length);
    const all = Array.isArray(pick?.moves) ? pick.moves : [];
    const moves = [
      ...new Set(
        all.filter(
          (m): m is string =>
            typeof m === 'string' && creature.learnset.includes(m) && pack.moves.has(m),
        ),
      ),
    ].slice(0, 4);
    if (moves.length !== all.length) dropped = true;
    picks.push({ id: creature.id, moves, edited: pick?.edited === true && moves.length > 0 });
  }
  const bagIds = bagItemIds();
  const bag = Array.isArray(raw.bag)
    ? [...new Set((raw.bag as unknown[]).filter((x): x is string => bagIds.includes(x as string)))]
    : empty.bag;
  // A chart entry follows its pick to the pick's new index; one naming a dropped pick goes; one
  // naming no saved pick at all is a chart that does not fit.
  let unfit = false;
  const remap = <T extends RosterSlot>(chart: unknown, slots: readonly T[]) => {
    const out: Partial<Record<T, number>> = {};
    if (!isChart(chart)) return out;
    for (const slot of slots) {
      const i = chart[slot];
      if (i === undefined) continue;
      const to = kept.get(i);
      if (to !== undefined) out[slot] = to;
      else if (i < 0 || i >= saved.length) unfit = true;
    }
    return out;
  };
  const offense = remap(raw.offense, OFFENSE_SLOTS);
  const defense = remap(raw.defense, DEFENSE_SLOTS);
  // A hold counts where it names a known slot with a pick in it, in slot order, each once.
  const held =
    raw.held && typeof raw.held === 'object' ? (raw.held as Record<string, unknown>) : {};
  const holds = <T extends RosterSlot>(
    x: unknown,
    slots: readonly T[],
    chart: Partial<Record<T, number>>,
  ) => (Array.isArray(x) ? slots.filter((s) => x.includes(s) && chart[s] !== undefined) : []);
  const state: RosterState = {
    gens: empty.gens,
    picks,
    offense,
    defense,
    held: {
      offense: holds(held.offense, OFFENSE_SLOTS, offense),
      defense: holds(held.defense, DEFENSE_SLOTS, defense),
    },
    bag: bag.slice(0, BAG_SIZE),
    // A saved preset choice (before slice 10c) is ignored: the AI picks its preset on Play.
    // A level the Match tab no longer offers (All-Pro since slice 10b) comes back as the default.
    level: MATCH_LEVELS.includes(raw.level as Difficulty) ? (raw.level as Difficulty) : empty.level,
  };
  if (unfit || !chartsFit(state)) {
    // A chart that does not fit: every hold released, then the proposal.
    return recommend({ ...state, held: { offense: [], defense: [] } }, pack);
  }
  if (dropped) {
    // Keep the player's edits and holds; only the open slots and unedited move sets are proposed.
    return recommend(state, pack);
  }
  return state;
}

/** Every chart entry names a pick, and no pick twice on a side. */
function chartsFit(state: RosterState): boolean {
  for (const chart of [state.offense, state.defense] as Record<string, number>[]) {
    const used = Object.values(chart);
    if (used.some((i) => i < 0 || i >= state.picks.length)) return false;
    if (new Set(used).size !== used.length) return false;
  }
  return true;
}
